"use client";

import { useState } from "react";
import { Percent } from "lucide-react";
import type { PricePoint } from "@/lib/data/types";
import { ChartCard } from "@/components/chart-card";
import type { ChartRangeLabel } from "@/components/charts/range-selector";
import { FairValueSimulator, type FairValueSimulatorProps } from "@/components/charts/fair-value-simulator";
import { DailyMultiplesChart, type DailyMultipleSeriesInput } from "@/components/charts/multi-kpi-line-chart";

/**
 * Zwei UNABHAENGIGE Zeitraum-States fuer Fairer-Wert-Simulator und Bewertungskennzahlen-
 * Verlauf im Bewertung-Tab (backlog.md -> [Punkt 50], Erik 2026-08-03: "die Zeitraum-
 * Auswahl von 'Fairer Wert & Kaufsimulator' soll unabhängig von der Zeitraum-Auswahl bei
 * 'Bewertungskennzahlen im Verlauf' sein").
 * **Loest eine fruehere, gegenteilige Entscheidung ab** (Erik-Feedback 2026-07-21:
 * "doppelte Zeitraum-Auswahl" - zwei RangeSelector nebeneinander wirkten damals redundant,
 * daher zuvor bewusst zu einem gemeinsamen State zusammengelegt). Eriks spaetere,
 * explizitere Anforderung wiegt hier schwerer als die aeltere Vereinfachung - beide
 * Charts bekommen wieder ihren eigenen, sichtbaren RangeSelector.
 * category-tabs.tsx ist eine async Server Component und kann selbst keinen State halten,
 * daher dieser kleine Client-Wrapper.
 */
export function ValuationTabCharts({
  fairValueSimulatorProps,
  priceHistory,
  dailyMultiplesSeries,
  symbol,
}: {
  fairValueSimulatorProps: Omit<FairValueSimulatorProps, "range" | "onRangeChange">;
  priceHistory: PricePoint[];
  dailyMultiplesSeries: DailyMultipleSeriesInput[];
  symbol: string;
}) {
  const [fairValueRange, setFairValueRange] = useState<ChartRangeLabel>("Max");
  const [multiplesRange, setMultiplesRange] = useState<ChartRangeLabel>("Max");

  return (
    <>
      <div>
        <h3 className="mb-3 text-sm font-medium text-ink-secondary">Fairer Wert & Kauf-Simulator</h3>
        <FairValueSimulator {...fairValueSimulatorProps} range={fairValueRange} onRangeChange={setFairValueRange} />
      </div>
      {dailyMultiplesSeries.length > 0 && priceHistory.length > 0 && (
        <ChartCard
          module={{
            key: "dailyMultiples",
            icon: <Percent className="h-4 w-4" />,
            title: "Bewertungskennzahlen im Verlauf",
            info: "KGV, KUV und KBV tagesgenau (Kurs geteilt durch die jeweils zuletzt bekannte Jahreskennzahl) mit Prognosezone aus Analystenschätzungen. KBV ist dabei eine Näherung, siehe Hinweis unter dem Chart.",
            exportFileName: `${symbol}-bewertungskennzahlen-verlauf`,
            chart: (
              <DailyMultiplesChart
                priceHistory={priceHistory}
                series={dailyMultiplesSeries}
                range={multiplesRange}
                onRangeChange={setMultiplesRange}
              />
            ),
          }}
        />
      )}
    </>
  );
}
